import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSession } from "../db";
import type { Session } from "../domain/types";
import { useLanguage } from "../state/language";
import { Button } from "../components/Button";
import { actionLabel } from "../domain/i18n";
import { modeStyle } from "../components/modeTheme";
import { Confetti } from "../components/Confetti";

export default function Stats() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const nav = useNavigate();
  const { lang, t } = useLanguage();
  const language = lang ?? "uk";
  const [sess, setSess] = useState<Session | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    (async () => setSess((await getSession(sessionId)) ?? null))();
  }, [sessionId]);

  const totals = useMemo(() => {
    const acc = { done: 0, failed: 0, skipped: 0, replaced: 0 };
    if (!sess) return acc;
    for (const e of sess.events) acc[e.action] += 1;
    return acc;
  }, [sess]);

  if (!sess) return <div className="pp-screen items-center justify-center">{t("loading")}</div>;

  // Party awards — the "remember when…" fuel. One title per superlative,
  // shown only when someone actually earned it. UA-only strings by design
  // (same pattern as helper labels in Session/ConsequenceStage).
  const awards = (
    [
      // Gender-neutral titles — any player name fits after them.
      { stat: "done", emoji: "🏆", label: "Зірка вечора" },
      { stat: "failed", emoji: "💀", label: "Легенда фейлів" },
      { stat: "timesAsHelper", emoji: "🤝", label: "Права рука" },
      { stat: "replaced", emoji: "🌀", label: "Сомельє карток" },
      // Public-skip accountability — dodges end up on the podium of shame.
      { stat: "skipped", emoji: "🙈", label: "Майстер ухилянь" },
    ] as const
  ).flatMap(({ stat, emoji, label }) => {
    let best: { name: string; n: number } | null = null;
    for (const p of sess.players) {
      const n = sess.playerStats[p.id]?.[stat] ?? 0;
      if (n > 0 && (!best || n > best.n)) best = { name: p.name, n };
    }
    return best ? [{ emoji, label, ...best }] : [];
  });

  const sortedPlayers = [...sess.players].sort((a, b) => {
    const dt =
      (sess.playerStats[b.id]?.timesSelected ?? 0) -
      (sess.playerStats[a.id]?.timesSelected ?? 0);
    if (dt !== 0) return dt;
    // Stable tie-breaker — alphabetical by name so order is deterministic.
    return a.name.localeCompare(b.name, "uk");
  });

  // Podium: top-3 by completed cards. Only shown when someone actually
  // finished something — a podium of zeros celebrates nothing.
  const podium = sortedByDone(sess).slice(0, 3);
  const showPodium = podium.length >= 2 && (podium[0]?.done ?? 0) > 0;

  return (
    <div className="pp-screen px-5" style={modeStyle(sess.modeId)}>
      {/* Session finished = the night's celebration moment. */}
      <Confetti count={32} />
      <div className="pt-8 text-center pp-flare relative">
        <div className="font-display text-6xl font-black tabular-nums">
          {sess.cardsSeen}
          <span className="opacity-50">/{sess.totalCards}</span>
        </div>
        <div className="opacity-70 mt-1">{t("stats_hero_label")}</div>
        <div className="mt-4 mx-auto h-2 w-48 bg-white/15 rounded-full overflow-hidden">
          <div
            className="h-full bg-white"
            style={{ width: `${Math.round((sess.cardsSeen / Math.max(1, sess.totalCards)) * 100)}%` }}
          />
        </div>
      </div>

      {/* Kahoot-style podium — bars rise staggered 3rd → 2nd → 1st. */}
      {showPodium && (
        <div className="mt-8 flex items-end justify-center gap-3">
          {[1, 0, 2].map((rank) => {
            const entry = podium[rank];
            if (!entry) return null;
            const bar = [
              { h: 116, delay: 500, medal: "🥇", bg: "rgba(251,191,36,0.28)", border: "rgba(251,191,36,0.6)" },
              { h: 84, delay: 250, medal: "🥈", bg: "rgba(255,255,255,0.16)", border: "rgba(255,255,255,0.4)" },
              { h: 60, delay: 50, medal: "🥉", bg: "rgba(217,119,6,0.22)", border: "rgba(217,119,6,0.5)" },
            ][rank]!;
            return (
              <div key={entry.id} className="flex flex-col items-center w-24">
                <div
                  className="pp-fade-up text-2xl"
                  style={{ animationDelay: `${bar.delay + 280}ms` }}
                  aria-hidden
                >
                  {bar.medal}
                </div>
                <div
                  className="pp-fade-up font-display font-bold text-sm truncate max-w-full"
                  style={{ animationDelay: `${bar.delay + 280}ms` }}
                >
                  {entry.name}
                </div>
                <div
                  className="pp-podium-rise w-full rounded-t-xl mt-1.5 flex items-start justify-center pt-1.5 text-sm font-black tabular-nums"
                  style={{
                    height: bar.h,
                    background: bar.bg,
                    border: `1px solid ${bar.border}`,
                    borderBottom: "none",
                    animationDelay: `${bar.delay}ms`,
                    boxShadow: rank === 0 ? "0 0 24px rgba(251,191,36,0.35)" : undefined,
                  }}
                >
                  {entry.done}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 grid grid-cols-4 gap-2">
        {(["done", "failed", "skipped", "replaced"] as const).map((k) => (
          <div key={k} className="pp-card-shell text-center px-2 py-3">
            <div className="text-2xl font-bold tabular-nums">{totals[k]}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-70 mt-1">
              {actionLabel(language, k)}
            </div>
          </div>
        ))}
      </div>

      {awards.length > 0 && (
        <div className="mt-8">
          <h3 className="text-[10px] uppercase tracking-[0.2em] opacity-50 mb-2">
            Титули вечора
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {awards.map((a) => (
              <div
                key={a.label}
                className="pp-card-shell px-3 py-3 flex items-center gap-2.5"
              >
                <span className="text-2xl shrink-0" aria-hidden>
                  {a.emoji}
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider opacity-60">
                    {a.label}
                  </div>
                  <div className="font-display font-bold truncate">{a.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <h3 className="font-display text-xl font-bold opacity-90">{t("stats_per_player")}</h3>
        <ul className="mt-3 flex flex-col gap-2">
          {sortedPlayers.map((p) => {
            const s = sess.playerStats[p.id] ?? {
              timesSelected: 0,
              done: 0,
              failed: 0,
              skipped: 0,
              replaced: 0,
              timesAsHelper: 0,
            };
            return (
              <li
                key={p.id}
                className="rounded-xl bg-white/5 border border-white/10 px-4 py-3 flex items-center gap-3"
              >
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center font-bold">
                  {p.name[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs opacity-70 mt-0.5 tabular-nums">
                    × {s.timesSelected} · ✓{s.done} · ✗{s.failed} · ↷{s.skipped} · ↻{s.replaced}
                    {s.timesAsHelper > 0 && (
                      <span className="opacity-80"> · 🤝 {s.timesAsHelper}</span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex-1" />
      <div className="pb-2 pt-4">
        <Button variant="primary" fullWidth onClick={() => nav("/modes", { replace: true })}>
          {t("stats_done")}
        </Button>
      </div>
    </div>
  );
}

/** Players ranked by completed cards (podium score), deterministic ties. */
function sortedByDone(sess: Session): { id: string; name: string; done: number }[] {
  return [...sess.players]
    .map((p) => ({ id: p.id, name: p.name, done: sess.playerStats[p.id]?.done ?? 0 }))
    .sort((a, b) => b.done - a.done || a.name.localeCompare(b.name, "uk"));
}
